import { Box, IconButton, styled, Typography } from "@mui/material";
import React, {
  useEffect,
  useState,
} from "react";
import Cropper from "react-easy-crop";
import {
  Area,
  Point,
} from "react-easy-crop/types";
import getCroppedImgV2, {
  createImage,
} from "utils/signature/cropping";
import {
  IMG_MAX_HEIGHT,
  IMG_MAX_WIDTH,
} from "utils/signature/imageFile";
import ActionButton from "../action-button/ActionButton";
import DialogTitleCloseButton from "../dialog-close-btn/DialogTitleCloseButton";
import {
  StewartDialog,
  StewartDialogActions,
  StewartDialogContent,
  StewartDialogTitle
} from "../dialogs/stewart-dialog/StewartDialog";
import ZoomImage from "./ZoomImage";
import { borderRadius, colors, fontSize, fontWeight, gradients, iconSize, padding } from "theme/defaultStyle";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "theme/icons/CloseIcon";

type Props = {
  open: boolean;
  imageUrl: string;
  onCrop: (image: string) => void;
  onCancel: () => void;
  renderNewSignatureStyledDialog?: boolean;
};

const initialValues = {
  STEP: 0.2,
  ZOOM: 1,
  CROP: { x: 0, y: 0 },
  ASPECT: 3 / 1,
};

// New styled components for signature dialog style
const StyledSignatureDialog = styled(StewartDialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxHeight: "calc(95vh)",
    maxWidth: "750px",
    minWidth: "700px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    padding: "36px 30px 30px 30px",
    overflow: "hidden",
  },
});

const StyledSignatureDialogTitle = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: "24px",
  marginBottom: "24px",
  flexShrink: 0,
  width: "100%",
  position: "relative",
});

const IconContainer = styled("div")({
  background: gradients.blueGradient09,
  borderRadius: borderRadius.large,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "56px",
  height: "56px",
  flexShrink: 0,
  "& .MuiSvgIcon-root": {
    color: colors.white,
    fontSize: iconSize.medium2,
  },
});

const StyledSignatureHeaderTitle = styled(Typography)({
  fontSize: fontSize.xlarge1,
  fontWeight: fontWeight.bold1,
  flex: 1,
});

const CloseIconContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "& .iconButton": {
    padding: padding.xsmall1,
    backgroundColor: colors.grey10,
  },
});

const StyledSignatureDialogContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  marginBottom: "16px",
  flex: "1 1 auto",
  overflow: "hidden",
  minHeight: 0,
});

const StyledSignatureDialogActions = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",
  flexShrink: 0,
  paddingTop: "8px",
});

export default function CropImage({ open, imageUrl, onCrop, onCancel, renderNewSignatureStyledDialog = false }: Props) {
  const [crop, setCrop] = useState<Point>(initialValues.CROP);
  const [zoom, setZoom] = useState<number>(initialValues.ZOOM);
  const [croppedAreaPixels, setCropperAreaPixels] = useState<Area>();
  const [aspect, setAspect] = useState<number>(initialValues.ASPECT);

  const handleCropCompleted = (_: Area, croppedAreaPixels: Area) =>
    setCropperAreaPixels(croppedAreaPixels);

  const handleZoomChange = (zoom: number) => setZoom(zoom);

  const handleCroppedImage = async () => {
    try {
      const croppedImage = await getCroppedImgV2(imageUrl, croppedAreaPixels);
      if (!croppedImage) throw new Error(`Unable to crop your image`);

      onCrop(croppedImage);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    setCrop(initialValues.CROP);
    setZoom(initialValues.ZOOM);
    setAspect(initialValues.ASPECT);
  }, [imageUrl]);

  useEffect(() => {
    if (!imageUrl) return;
    const getAspect = async () => {
      try {
        const { width, height } = await createImage(imageUrl);
        if (width <= IMG_MAX_WIDTH && height <= IMG_MAX_HEIGHT) {
          setAspect(width / height);
        }
      } catch (error) {
        console.error("There was an error loading the selected image. ", error);
      }
    };

    getAspect();
  }, [imageUrl]);

  // Render signature-style dialog
  if (renderNewSignatureStyledDialog) {
    return (
      <Box>
        <StyledSignatureDialog
          open={open}
          maxWidth="md"
        >
          <StyledSignatureDialogTitle>
            <IconContainer>
              <AddIcon />
            </IconContainer>
            <StyledSignatureHeaderTitle>Add New Signature</StyledSignatureHeaderTitle>
            <CloseIconContainer>
              <IconButton
                className="iconButton"
                onClick={onCancel}
              >
                <CloseIcon />
              </IconButton>
            </CloseIconContainer>
          </StyledSignatureDialogTitle>
          <StyledSignatureDialogContent>
            <Box
              position="relative"
              width="100%"
              height="400px"
              sx={{
                backgroundColor: colors.grey10,
                borderRadius: borderRadius.small,
                flexShrink: 0,
              }}
            >
              <Cropper
                showGrid={false}
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                restrictPosition={false}
                onCropChange={setCrop}
                onZoomChange={(zoom: number) =>
                  setZoom(zoom - initialValues.STEP)
                }
                onCropComplete={handleCropCompleted}
                zoomWithScroll={false}
              />
            </Box>
            <Box sx={{ flexShrink: 0 }}>
              <ZoomImage
                value={zoom}
                onZoom={handleZoomChange}
                step={initialValues.STEP}
                min={initialValues.STEP}
                max={initialValues.STEP * 9}
              />
            </Box>
          </StyledSignatureDialogContent>
          <StyledSignatureDialogActions>
            <ActionButton
              color="primary"
              variant="contained"
              onClick={handleCroppedImage}
            >
              Done
            </ActionButton>
          </StyledSignatureDialogActions>
        </StyledSignatureDialog>
      </Box>
    );
  }

  // Render original dialog style (existing implementation)
  return (
    <Box>
      <StewartDialog
        open={open}
        maxWidth="md"
      >
        <StewartDialogTitle type={"INFO"}>
          Image Upload
          <DialogTitleCloseButton onClick={onCancel}/>
        </StewartDialogTitle>
        <StewartDialogContent>
          <Box position="relative" width={500} height={300}>
            <Cropper
              showGrid={false}
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              restrictPosition={false}
              onCropChange={setCrop}
              onZoomChange={(zoom: number) =>
                setZoom(zoom - initialValues.STEP)
              }
              onCropComplete={handleCropCompleted}
              zoomWithScroll={false}
            />
          </Box>
          <ZoomImage
            value={zoom}
            onZoom={handleZoomChange}
            step={initialValues.STEP}
            min={initialValues.STEP} // 0.2
            max={initialValues.STEP * 9} // 1.8
          />
        </StewartDialogContent>
        <StewartDialogActions>
          <ActionButton
            color="primary"
            variant="outlined"
            onClick={handleCroppedImage}
          >
            Ok
          </ActionButton>
        </StewartDialogActions>
      </StewartDialog>
    </Box>
  );
}
